import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Users, 
  Calendar, 
  DollarSign,
  AlertTriangle,
  Clock,
  CheckCircle,
  Target,
  TrendingUp,
  Briefcase,
  User,
  Tag,
  Eye,
  Edit,
  Pause,
  Play,
  X
} from "lucide-react";

interface ProjectPortfolioProps {
  onNavigateBack: () => void;
}

interface Project {
  id: string;
  title: string;
  description: string;
  owner: string;
  phase: "Planning" | "Initiation" | "Execution" | "Monitoring" | "Closure";
  status: "In Progress" | "Planning" | "On Hold" | "Critical" | "Completed" | "Cancelled";
  startDate: string;
  endDate: string;
  budget: {
    planned: number;
    allocated: number;
    spent: number;
  };
  teamSize: number;
  progress: number;
  tags: string[];
  priority: "Low" | "Medium" | "High" | "Critical";
  riskLevel: "Low" | "Medium" | "High" | "Critical";
  originatingDemandId?: string;
  warningConditions?: string[];
}

interface NewProjectForm {
  title: string;
  description: string;
  owner: string;
  startDate: string;
  endDate: string;
  budget: string;
  teamSize: string;
  priority: "Low" | "Medium" | "High" | "Critical";
  tags: string;
  originatingDemandId: string;
}

const mockProjects: Project[] = [
  {
    id: "PRJ-001",
    title: "Digital Transformation Initiative",
    description: "Company-wide digital transformation to modernize core business processes and customer experiences",
    owner: "Sarah Johnson",
    phase: "Execution",
    status: "In Progress",
    startDate: "2024-01-15",
    endDate: "2025-06-30",
    budget: {
      planned: 2500000,
      allocated: 2500000,
      spent: 1200000
    },
    teamSize: 12,
    progress: 48,
    tags: ["Digital", "Transformation", "Strategic"],
    priority: "Critical",
    riskLevel: "Medium",
    originatingDemandId: "DEM-001"
  },
  {
    id: "PRJ-002",
    title: "Customer Portal Enhancement",
    description: "Upgrade customer self-service portal with new features and improved user experience",
    owner: "Michael Chen",
    phase: "Execution",
    status: "In Progress",
    startDate: "2024-03-01",
    endDate: "2024-12-15",
    budget: {
      planned: 850000,
      allocated: 850000,
      spent: 425000
    },
    teamSize: 8,
    progress: 65,
    tags: ["Customer", "Portal", "UX"],
    priority: "High",
    riskLevel: "Low"
  },
  {
    id: "PRJ-003",
    title: "Supply Chain Optimization",
    description: "Implementation of AI-driven supply chain management system",
    owner: "Amanda Rodriguez",
    phase: "Planning",
    status: "On Hold",
    startDate: "2024-04-01",
    endDate: "2024-11-30",
    budget: {
      planned: 1200000,
      allocated: 600000,
      spent: 50000
    },
    teamSize: 6,
    progress: 15,
    tags: ["Supply Chain", "AI", "Optimization"],
    priority: "Medium",
    riskLevel: "High",
    warningConditions: ["Project on hold due to vendor selection delays", "Budget review required"]
  },
  {
    id: "PRJ-004",
    title: "Data Analytics Platform",
    description: "Enterprise-wide data analytics and business intelligence platform implementation",
    owner: "Robert Davis",
    phase: "Monitoring",
    status: "Critical",
    startDate: "2023-09-01",
    endDate: "2024-12-31",
    budget: {
      planned: 1800000,
      allocated: 1800000,
      spent: 1650000
    },
    teamSize: 10,
    progress: 85,
    tags: ["Analytics", "BI", "Data"],
    priority: "Critical",
    riskLevel: "Critical",
    warningConditions: ["Budget overrun risk", "Key stakeholder concerns raised"]
  },
  {
    id: "PRJ-005",
    title: "Mobile App Development",
    description: "Native mobile application for iOS and Android platforms",
    owner: "Lisa Wang",
    phase: "Execution",
    status: "In Progress",
    startDate: "2024-02-15",
    endDate: "2024-09-30",
    budget: {
      planned: 650000,
      allocated: 650000,
      spent: 390000
    },
    teamSize: 7,
    progress: 70,
    tags: ["Mobile", "iOS", "Android"],
    priority: "High",
    riskLevel: "Low"
  }
];

const mockDemands = [
  { id: "DEM-001", title: "Digital Transformation Initiative" },
  { id: "DEM-002", title: "Customer Experience Enhancement" },
  { id: "DEM-003", title: "Process Automation" },
  { id: "DEM-004", title: "Security Infrastructure Upgrade" }
];

export function ProjectPortfolio({ onNavigateBack }: ProjectPortfolioProps) {
  const [projects, setProjects] = useState<Project[]>(mockProjects);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState<NewProjectForm>({
    title: "",
    description: "",
    owner: "",
    startDate: "",
    endDate: "",
    budget: "",
    teamSize: "",
    priority: "Medium",
    tags: "",
    originatingDemandId: "none"
  });

  // Calculate metrics
  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => p.status === "In Progress").length;
  const totalBudgetPlanned = projects.reduce((sum, p) => sum + p.budget.planned, 0);
  const totalBudgetAllocated = projects.reduce((sum, p) => sum + p.budget.allocated, 0);
  const budgetAllocationPercentage = totalBudgetPlanned > 0 ? (totalBudgetAllocated / totalBudgetPlanned) * 100 : 0;
  const atRiskProjects = projects.filter(p => p.status === "Critical" || p.riskLevel === "Critical" || p.riskLevel === "High").length;

  const getStatusColor = (status: Project["status"]) => {
    switch (status) {
      case "In Progress": return "bg-blue-100 text-blue-800 border-blue-200";
      case "Planning": return "bg-purple-100 text-purple-800 border-purple-200";
      case "On Hold": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Critical": return "bg-red-100 text-red-800 border-red-200";
      case "Completed": return "bg-green-100 text-green-800 border-green-200";
      case "Cancelled": return "bg-gray-100 text-gray-800 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPhaseColor = (phase: Project["phase"]) => {
    switch (phase) {
      case "Planning": return "bg-slate-100 text-slate-800 border-slate-200";
      case "Initiation": return "bg-blue-100 text-blue-800 border-blue-200";
      case "Execution": return "bg-orange-100 text-orange-800 border-orange-200";
      case "Monitoring": return "bg-green-100 text-green-800 border-green-200";
      case "Closure": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityColor = (priority: Project["priority"]) => {
    switch (priority) {
      case "Critical": return "bg-red-100 text-red-800 border-red-200";
      case "High": return "bg-orange-100 text-orange-800 border-orange-200";
      case "Medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRiskColor = (riskLevel: Project["riskLevel"]) => {
    switch (riskLevel) {
      case "Critical": return "text-red-600";
      case "High": return "text-orange-600";
      case "Medium": return "text-yellow-600";
      case "Low": return "text-green-600";
      default: return "text-gray-600";
    }
  };

  const handleNewProjectSubmit = () => {
    if (!newProjectForm.title || !newProjectForm.owner || !newProjectForm.startDate || !newProjectForm.endDate) {
      toast.error("Please fill in all required fields");
      return;
    }

    const newProject: Project = {
      id: `PRJ-${String(projects.length + 1).padStart(3, '0')}`,
      title: newProjectForm.title,
      description: newProjectForm.description,
      owner: newProjectForm.owner,
      phase: "Planning",
      status: "Planning",
      startDate: newProjectForm.startDate,
      endDate: newProjectForm.endDate,
      budget: {
        planned: parseFloat(newProjectForm.budget) || 0,
        allocated: 0,
        spent: 0
      },
      teamSize: parseInt(newProjectForm.teamSize) || 1,
      progress: 0,
      tags: newProjectForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      priority: newProjectForm.priority,
      riskLevel: "Low",
      originatingDemandId: newProjectForm.originatingDemandId && newProjectForm.originatingDemandId !== "none" ? newProjectForm.originatingDemandId : undefined
    };

    setProjects(prev => [...prev, newProject]);
    setIsNewProjectOpen(false);
    setNewProjectForm({
      title: "",
      description: "",
      owner: "",
      startDate: "",
      endDate: "",
      budget: "",
      teamSize: "",
      priority: "Medium",
      tags: "",
      originatingDemandId: "none"
    });

    toast.success("Project created successfully!", {
      description: "Your new project has been added to the portfolio and is ready for planning."
    });
  };

  const handleProjectAction = (projectId: string, action: "pause" | "resume" | "view") => {
    switch (action) {
      case "pause":
        setProjects(prev => 
          prev.map(p => 
            p.id === projectId ? { ...p, status: "On Hold" as const } : p
          )
        );
        toast.success("Project paused", {
          description: "Project has been put on hold"
        });
        break;
      case "resume":
        setProjects(prev => 
          prev.map(p => 
            p.id === projectId ? { ...p, status: "In Progress" as const } : p
          )
        );
        toast.success("Project resumed", {
          description: "Project is now active"
        });
        break;
      case "view":
        toast.info("Project details", {
          description: "Opening detailed project view..."
        });
        break;
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={onNavigateBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl">Project Portfolio</h1>
              <p className="text-muted-foreground">
                Manage and track all organizational projects
              </p>
            </div>
          </div>
          
          <Dialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
                <DialogDescription>
                  Fill out the project details below to create a new project in your portfolio. Required fields are marked with an asterisk (*).
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Project Title *</Label>
                    <Input
                      id="title"
                      value={newProjectForm.title}
                      onChange={(e) => setNewProjectForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter project title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner">Project Owner *</Label>
                    <Input
                      id="owner"
                      value={newProjectForm.owner}
                      onChange={(e) => setNewProjectForm(prev => ({ ...prev, owner: e.target.value }))}
                      placeholder="Enter project owner name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newProjectForm.description}
                    onChange={(e) => setNewProjectForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter project description"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={newProjectForm.startDate}
                      onChange={(e) => setNewProjectForm(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date *</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={newProjectForm.endDate}
                      onChange={(e) => setNewProjectForm(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget ($)</Label>
                    <Input
                      id="budget"
                      type="number"
                      value={newProjectForm.budget}
                      onChange={(e) => setNewProjectForm(prev => ({ ...prev, budget: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="teamSize">Team Size</Label>
                    <Input
                      id="teamSize"
                      type="number"
                      value={newProjectForm.teamSize}
                      onChange={(e) => setNewProjectForm(prev => ({ ...prev, teamSize: e.target.value }))}
                      placeholder="1"
                      min="1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select 
                      value={newProjectForm.priority} 
                      onValueChange={(value: "Low" | "Medium" | "High" | "Critical") => 
                        setNewProjectForm(prev => ({ ...prev, priority: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="demandId">Originating Demand (Optional)</Label>
                    <Select 
                      value={newProjectForm.originatingDemandId} 
                      onValueChange={(value) => 
                        setNewProjectForm(prev => ({ ...prev, originatingDemandId: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select demand" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {mockDemands.map((demand) => (
                          <SelectItem key={demand.id} value={demand.id}>
                            {demand.id} - {demand.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    value={newProjectForm.tags}
                    onChange={(e) => setNewProjectForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="e.g. Digital, Customer, Strategic"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsNewProjectOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleNewProjectSubmit}>
                  Create Project
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* High-level Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Total Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalProjects}</div>
              <p className="text-xs text-muted-foreground">Across all phases</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="h-4 w-4" />
                Active Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{activeProjects}</div>
              <p className="text-xs text-muted-foreground">Currently in progress</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Budget
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${(totalBudgetPlanned / 1000000).toFixed(1)}M</div>
              <p className="text-xs text-muted-foreground">
                {budgetAllocationPercentage.toFixed(0)}% allocated
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                At-Risk Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{atRiskProjects}</div>
              <p className="text-xs text-muted-foreground">Require attention</p>
            </CardContent>
          </Card>
        </div>

        {/* Active Projects Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Active Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {projects.map((project) => (
                <div key={project.id} className="border rounded-lg p-6 space-y-4">
                  {/* Project Header */}
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{project.title}</h3>
                        <Badge variant="outline" className="text-xs">
                          {project.id}
                        </Badge>
                        <Badge variant="outline" className={`${getPhaseColor(project.phase)} text-xs`}>
                          {project.phase}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{project.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${getStatusColor(project.status)} text-xs`}>
                        {project.status}
                      </Badge>
                      <Badge variant="outline" className={`${getPriorityColor(project.priority)} text-xs`}>
                        {project.priority} Priority
                      </Badge>
                    </div>
                  </div>

                  {/* Warning Banners */}
                  {project.warningConditions && project.warningConditions.length > 0 && (
                    <div className="space-y-2">
                      {project.warningConditions.map((condition, index) => (
                        <Alert key={index} className="border-yellow-200 bg-yellow-50">
                          <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          <AlertDescription className="text-yellow-800">
                            {condition}
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  )}

                  {/* Project Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Timeline */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Timeline
                      </Label>
                      <div className="text-sm">
                        <div>Start: {new Date(project.startDate).toLocaleDateString()}</div>
                        <div>End: {new Date(project.endDate).toLocaleDateString()}</div>
                      </div>
                    </div>

                    {/* Budget */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        Budget
                      </Label>
                      <div className="text-sm">
                        <div>Planned: ${project.budget.planned.toLocaleString()}</div>
                        <div>Spent: ${project.budget.spent.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">
                          {project.budget.planned > 0 ? 
                            `${((project.budget.spent / project.budget.planned) * 100).toFixed(0)}% utilized` :
                            'No budget set'
                          }
                        </div>
                      </div>
                    </div>

                    {/* Team */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        Team
                      </Label>
                      <div className="text-sm">
                        <div>Owner: {project.owner}</div>
                        <div>Size: {project.teamSize} members</div>
                        <div className={`text-xs ${getRiskColor(project.riskLevel)}`}>
                          {project.riskLevel} Risk
                        </div>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        Progress
                      </Label>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{project.progress}%</span>
                          <span className="text-xs text-muted-foreground">Complete</span>
                        </div>
                        <Progress value={project.progress} className="h-2" />
                      </div>
                    </div>
                  </div>

                  {/* Tags and Actions */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-2">
                      {project.tags.length > 0 && (
                        <>
                          <Tag className="h-3 w-3 text-muted-foreground" />
                          <div className="flex gap-1">
                            {project.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {project.tags.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{project.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        </>
                      )}
                      {project.originatingDemandId && (
                        <div className="text-xs text-muted-foreground ml-4">
                          From: {project.originatingDemandId}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {project.status === "On Hold" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleProjectAction(project.id, "resume")}
                          className="flex items-center gap-1"
                        >
                          <Play className="h-3 w-3" />
                          Resume
                        </Button>
                      ) : project.status === "In Progress" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleProjectAction(project.id, "pause")}
                          className="flex items-center gap-1"
                        >
                          <Pause className="h-3 w-3" />
                          Pause
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleProjectAction(project.id, "view")}
                        className="flex items-center gap-1"
                      >
                        <Eye className="h-3 w-3" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}